<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Button from "$app/components/ButtonSpinner.svelte"
  import { ListPlus, ListCheck, ListX, SquareCheckBig } from "lucide-svelte"
  import BookReadingsForm from "./BookReadingsForm.svelte"
  import { appStore } from "$app/store.svelte"

  import amazon_logo from "@images/university/amazon-logo.png"

  interface Props {
    book_id: number
    onplanbook?: (plan_book: PlanBookType) => void
  }

  let { book_id, onplanbook = () => {} }: Props = $props()

  let current_book: BookType = appStore.current_book
  let current_user: UserType = appStore.current_user
  let admin_university: Boolean = appStore.admin_university

  let isLoading = $state(true)
  let isLoadingReadings = $state(false)
  let editReadings = $state(false)

  let book: BookType = $state(null)

  async function loadBook(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get<{ book: BookType; plan_book: PlanBookType }>(
        `/plans/${appStore.plan.id}/books/${book_id}.json`
      )

      book = resp.data.book
      if (resp.data.plan_book) {
        book.plan_book = resp.data.plan_book
      }
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal", "error")
    } finally {
      isLoading = false
    }
  }

  async function loadBookReadings(): Promise<void> {
    isLoadingReadings = true
    try {
      const resp = await xhr.get<BookReadingType[]>(
        `/plans/${appStore.plan.id}/books/${book_id}/book_readings.json`
      )

      let book_readings = resp.data
      book.book_readings = book_readings
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal", "error")
    } finally {
      isLoadingReadings = false
    }
  }

  onMount(() => {
    loadBook()
  })

  let savingStatus = $state(false)
  let savingReadLater = $state(false)

  async function savePlanBook(
    data: Record<string, string | boolean>
  ): Promise<void> {
    if (data.hasOwnProperty("read_later")) {
      savingReadLater = true
    } else if (data.hasOwnProperty("status")) {
      savingStatus = true
    }

    try {
      const resp = book.plan_book
        ? await xhr.put<PlanBookType>(
            `/plans/${appStore.plan.id}/plan_books/${book.plan_book.id}.json`,
            {
              plan_book: data
            }
          )
        : await xhr.post<PlanBookType>(
            `/plans/${appStore.plan.id}/plan_books.json`,
            {
              plan_book: { book_id: book.id, ...data }
            }
          )

      let planBook = resp.data
      book = { ...book, plan_book: planBook }
      //console.log("planbook", planBook)
      onplanbook(planBook)

      if (data.status === "in_progress") {
        toast("You are now reading this book", "success")
      } else if (data.status == "marked_as_read") {
        toast("The book has been marked as read", "success")
      } else if (data.status == "completed") {
        toast("The book has been marked as completed", "success")
      }
    } catch (e: any) {
      console.log(e)
      toast("There was an error updating this book", "error")
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    } finally {
      book = book
      if (data.hasOwnProperty("read_later")) {
        savingReadLater = false
      } else if (data.hasOwnProperty("status")) {
        savingStatus = false
      }
    }
  }

  function confirmMarkAsRead() {
    if (confirm("Are you sure you want to mark this book as read?")) {
      savePlanBook({ status: "marked_as_read" })
    }
  }

  function addedBookReadings() {
    loadBookReadings()
    editReadings = false
  }
</script>

{#if editReadings}
  <BookReadingsForm bind:book mode={"edit"} onadd={addedBookReadings} />
{:else if isLoading}
  <LoadingSpinner />
{:else}
  {#key book}
    <div class="grid grid-cols-2">
      <div>
        <img
          src={book.image_url}
          class="object-cover w-full"
          alt={book.title}
        />
      </div>
      <div class="px-4">
        <p class="text-xl lg:text-3xl">{book.title}</p>
        {#if book.formatted_date}
          <p class="text-sm mt-2">
            {book.formatted_date} &sdot; {book.edition}
          </p>
        {/if}

        <div class="flex justify-between mt-4 items-center">
          {#if admin_university}
            {#if book?.plan_book?.read_later}
              <Button
                processing={savingReadLater}
                onclick={() => savePlanBook({ read_later: false })}
                class="mr-6 text-sm py-2 px-4 text-gray-700 hover:text-gray-600 border-2 rounded flex items-center justify-center hover:border-gray-100 border-blue-600 bg-blue-100 hover:border-red-400 hover:bg-red-300 hover:shadow-lg"
              >
                <ListX class="size-5 text-gray-700 hover:text-gray-600 mr-2" />
                <span>Remove Read Later</span>
              </Button>
            {:else}
              <Button
                processing={savingReadLater}
                onclick={() => savePlanBook({ read_later: true })}
                class="mr-6 text-sm py-2 px-4 text-gray-700 border-2 border-gray-100 rounded flex items-center justify-center hover:border-blue-600 hover:bg-blue-100 hover:shadow-lg"
              >
                <ListPlus
                  class="size-5 text-gray-700 hover:text-gray-800 mr-2"
                />
                <span>Read Later</span>
              </Button>
            {/if}

            {#if book?.plan_book?.status !== "completed" && book?.plan_book?.status !== "marked_as_read"}
              <Button
                processing={savingStatus}
                onclick={confirmMarkAsRead}
                class="text-sm py-2 px-4 text-gray-700 hover:bg-green-100 flex border-2 border-gray-100 rounded"
              >
                <SquareCheckBig
                  class="size-5 text-gray-700 hover:text-gray-800 mr-2"
                />
                <span>Mark as Read</span>
              </Button>
            {/if}
          {/if}
        </div>
        {#if current_book && book.id !== current_book.id}
          <div
            class="mt-4 bg-gray-200 text-black text-base rounded py-2 px-4 shadow"
          >
            <div class="font-bold text-left">Currently Reading</div>
            <div class="text-sm mt-2">{current_book.title}</div>
          </div>
        {/if}
        <div class="flex justify-start items-start mt-4">
          {#if book.affiliate_link}
            <a
              href={book.affiliate_link}
              target="_blank"
              class="bg-quarterly-goal rounded-lg py-2 px-4 text-black text-sm hover:underline hover:text-gray-800 flex items-center justify-center mr-2 whitespace-nowrap"
              rel="noreferrer"
            >
              <img
                src={amazon_logo}
                class="h-4 w-auto mr-2"
                alt="Purchase Book"
              />
              Purchase Book *</a
            >
          {/if}
          {#if book?.plan_book?.status === "in_progress"}
            <span
              class="bg-green-600 text-white text-lg font-bold rounded-lg py-2 px-4"
              >You are reading this book</span
            >
          {:else if book?.plan_book?.status === "completed" || book?.plan_book?.status === "marked_as_read"}
            <span
              class="bg-green-100 text-green-600 text-sm rounded-lg py-2 px-4 flex items-center"
            >
              <ListCheck class="size-4 text-green-500 mr-2" />
              Completed Book</span
            >
          {:else if !current_book && admin_university}
            <Button
              processing={savingStatus}
              onclick={() => savePlanBook({ status: "in_progress" })}
              class="bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg py-2 px-4 flex items-center justify-center"
            >
              Start Reading This Book
            </Button>
          {/if}
        </div>

        <div
          class="mt-4 border border-gray-100 p-2 rounded-sm overflow-y-auto shadow-sm {book
            .book_readings.length > 0
            ? 'max-h-36'
            : 'max-h-72'}"
        >
          <p class="text-sm prose prose-neutral">
            {@html book.description}
          </p>
        </div>

        <p class="text-sm font-bold mt-4 flex items-center justify-between">
          Reading Assignments ({book.book_readings.length})
          {#if book.creator_user_id === current_user?.id}
            <button
              onclick={() => (editReadings = true)}
              class="text-sm text-gray-600 py-1 px-3 border-gray-200 border hover:bg-gray-100 rounded"
              >Edit Readings</button
            >
          {/if}
        </p>
        {#if isLoadingReadings}
          <LoadingSpinner />
        {:else if book.book_readings && book.book_readings.length > 0}
          <ul
            class="mt-2 border border-gray-300 rounded max-h-36 overflow-y-scroll"
          >
            {#each book.book_readings as book_reading, i}
              <li
                class="flex justify-start items-center py-1 border-b border-gray-200 mx-4"
              >
                <div class="text-gray-400 text-lg pr-3">{i + 1}</div>
                <div class="text-sm text-black px-4">
                  {book_reading.title}
                </div>
              </li>
            {/each}
          </ul>
        {:else}
          <div
            class="mt-2 border border-gray-300 rounded py-2 px-5 text-gray-600"
          >
            No reading assignments configured for this book
          </div>
        {/if}

        <div class="mt-4">
          {#each book.categories as category}
            <span
              class="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 mr-2"
            >
              {category}
            </span>
          {/each}
        </div>
      </div>
    </div>
    {#if book.affiliate_link}
      <p class="text-xs italic text-gray-700 mt-4">
        * As an Amazon Associate Lead With Purpose earns from qualifying
        purchases.
      </p>
    {/if}
  {/key}
{/if}
